import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "../css/gigs.css";
import ReactDOM from "react-dom";
import AdminMenu from './adminMenu';
import { routeMiles } from './routeMiles';

// Same-origin ("/api"): the login cookie is only sent to the address you are on, so
// calling a different address (localhost:7022 from :3000, or www from the bare domain)
// would arrive logged out. In dev, package.json's "proxy" forwards /api to the server.
const API_BASE_URL = "/api";

// Today's date as yyyy-MM-dd in the browser's local time (what a date input expects).
// (toISOString() would give the UTC date, which is "tomorrow" in Phoenix after 5 PM.)
const todayLocal = () => new Date().toLocaleDateString("en-CA");

export default function GigsTable({ setIsAuthenticated }) {
    const [gigs, setGigs] = useState([]);

    // TOGGLE STATE
    const [showPastGigs, setShowPastGigs] = useState(false);

    const emptyGig = {
        title: "",
        clientName: "",
        clientPhone: "",
        clientEmail: "",
        venueName: "",
        venueAddress: "",
        startDateTime: "",
        endDateTime: "",
        payAmount: "",
        depositAmount: "",
        depositPaid: false,
        depositPaidDate: "",
        fullyPaid: false,
        fullyPaidDate: "",
        status: "",
        notes: "",
        privateNotes: ""
    };

    const [formGig, setFormGig] = useState(emptyGig);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedGigId, setSelectedGigId] = useState(null);

    // MILEAGE (for taxes)
    const [mileageMap, setMileageMap] = useState({});          // gigId -> saved record
    const [showMileage, setShowMileage] = useState(false);
    const [mileageYear, setMileageYear] = useState(new Date().getFullYear());
    const [includeUpcoming, setIncludeUpcoming] = useState(false);
    const [recalcAll, setRecalcAll] = useState(false);
    const [mileageOrigin, setMileageOrigin] = useState(() => {
        try { return localStorage.getItem("mileageOrigin") || ""; } catch { return ""; }
    });
    const [mileageRows, setMileageRows] = useState([]);
    const [mileageRunning, setMileageRunning] = useState(false);
    const [mileageMsg, setMileageMsg] = useState("");
    const stopMileage = useRef(false);

    // Load gigs
    useEffect(() => {
        fetchGigs();
        fetchMileage();
    }, []);

    const fetchGigs = async () => {
        try {
            console.log("FETCH URL:", `${API_BASE_URL}/Gigs`);

            const res = await axios.get(`${API_BASE_URL}/Gigs`);

            setGigs(res.data);
        } catch (err) {
            console.error("FETCH ERROR:", err.response || err);
            alert("Could not fetch gigs from the server.");
        }
    };

    const openAddModal = () => {
        setFormGig(emptyGig);
        setIsEdit(false);
        setSelectedGigId(null);
        setShowModal(true);
    };

    const openEditModal = (gig) => {
        setFormGig({
            ...gig,
            startDateTime: gig.startDateTime?.slice(0, 16),
            endDateTime: gig.endDateTime?.slice(0, 16),
            // A date input only accepts yyyy-MM-dd, so trim the saved values
            // (they were showing up blank before)
            depositPaidDate: gig.depositPaidDate?.slice(0, 10) || "",
            fullyPaidDate: gig.fullyPaidDate?.slice(0, 10) || ""
        });

        setSelectedGigId(gig.id);
        setIsEdit(true);
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormGig((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSaveGig = async (e) => {
        e.preventDefault();

        const gigToSend = {
            ...(isEdit && { Id: selectedGigId }),
            Title: formGig.title || "",
            ClientName: formGig.clientName || "",
            ClientPhone: formGig.clientPhone || "",
            ClientEmail: formGig.clientEmail || "",
            VenueName: formGig.venueName || "",
            VenueAddress: formGig.venueAddress || "",
            StartDateTime: formGig.startDateTime,
            EndDateTime: formGig.endDateTime || null,
            PayAmount: formGig.payAmount
                ? Number(formGig.payAmount)
                : null,
            DepositAmount: formGig.depositAmount
                ? Number(formGig.depositAmount)
                : null,
            DepositPaid: formGig.depositPaid,
            // Keep the date already saved (or typed in the form). Only fall back to
            // today when the box is ticked and there is no date yet. It used to be
            // reset to "now" on every save.
            DepositPaidDate: formGig.depositPaid
                ? (formGig.depositPaidDate || todayLocal())
                : null,
            FullyPaid: formGig.fullyPaid,
            FullyPaidDate: formGig.fullyPaid
                ? (formGig.fullyPaidDate || todayLocal())
                : null,
            Status: formGig.status || "",
            Notes: formGig.notes || "",
            PrivateNotes: formGig.privateNotes || ""
        };

        console.log(
            "Sending to API:",
            isEdit
                ? `${API_BASE_URL}/gigs/${selectedGigId}`
                : `${API_BASE_URL}/gigs`
        );

        console.log("Payload:", gigToSend);

        try {
            if (isEdit) {
                await axios.post(
                    `${API_BASE_URL}/Gigs/${selectedGigId}`,
                    gigToSend
                );
            } else {
                await axios.post(`${API_BASE_URL}/Gigs`, gigToSend);
            }

            fetchGigs();
            closeModal();
        } catch (err) {
            console.error("SAVE ERROR:", err);

            if (err.response) {
                console.log("Status:", err.response.status);
                console.log("Data:", err.response.data);
            }

            alert("Could not save gig. Check console for details.");
        }
    };

    const handleUploadClick = (gigId) => {
        document.getElementById(`file-${gigId}`).click();
    };

    const handleFileChange = async (e, gigId) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("gigId", gigId);

        try {
            await axios.post(`${API_BASE_URL}/Gigs/upload-contract`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });

            e.target.value = "";
            alert("Contract uploaded successfully");
            fetchGigs();
        } catch (err) {
            console.error(err);
            // The server says why (not a PDF, over 10 MB, gig not found), so show it
            const reason =
                typeof err.response?.data === "string" ? err.response.data : "";
            alert(reason ? `Upload failed: ${reason}` : "Upload failed");
            e.target.value = "";
        }
    };

    // ---------- MILEAGE ----------
    const fetchMileage = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/Mileage`);
            const map = {};
            (res.data || []).forEach((m) => {
                map[m.gigId] = m;
            });
            setMileageMap(map);
        } catch (err) {
            console.error("MILEAGE FETCH ERROR:", err.response || err);
        }
    };

    // One row per gig in the chosen year (finished gigs only, unless "include upcoming")
    const buildMileageRows = (year, upcoming) => {
        const nowDate = new Date();
        return gigs
            .filter((g) => {
                if (!g.startDateTime) return false;
                const d = new Date(g.startDateTime);
                if (d.getFullYear() !== Number(year)) return false;
                return upcoming || d <= nowDate;
            })
            .sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime))
            .map((g) => {
                const saved = mileageMap[g.id];
                const hasAddress = !!(g.venueAddress && g.venueAddress.trim());
                return {
                    gigId: g.id,
                    date: g.startDateTime,
                    venueName: g.venueName,
                    venueAddress: g.venueAddress,
                    miles: saved ? String(saved.milesOneWay) : "",
                    status: saved ? "saved" : hasAddress ? "pending" : "noaddr",
                    error: ""
                };
            });
    };

    // Rebuild the list when the modal opens or the year / checkbox changes
    useEffect(() => {
        if (showMileage && !mileageRunning) {
            setMileageRows(buildMileageRows(mileageYear, includeUpcoming));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMileage, mileageYear, includeUpcoming]);

    const openMileageModal = () => {
        if (!mileageOrigin) {
            const withOrigin = Object.values(mileageMap).find((m) => m.originAddress);
            if (withOrigin) setMileageOrigin(withOrigin.originAddress);
        }
        setMileageMsg("");
        setShowMileage(true);
    };

    const closeMileageModal = () => {
        stopMileage.current = true;
        setShowMileage(false);
    };

    const updateMileageRow = (gigId, patch) =>
        setMileageRows((prev) =>
            prev.map((r) => (r.gigId === gigId ? { ...r, ...patch } : r))
        );

    // Works out the miles for every gig in the list, one after another
    const runMileage = async () => {
        const origin = mileageOrigin.trim();
        if (!origin) {
            setMileageMsg("Enter the address you drove from.");
            return;
        }
        try { localStorage.setItem("mileageOrigin", origin); } catch { /* ignore */ }

        stopMileage.current = false;
        setMileageRunning(true);
        setMileageMsg("");

        const todo = mileageRows.filter(
            (r) =>
                r.status !== "noaddr" &&
                (recalcAll || r.status === "pending" || r.status === "error")
        );

        for (const row of todo) {
            if (stopMileage.current) break;
            updateMileageRow(row.gigId, { status: "working", error: "" });
            try {
                const res = await axios.post(`${API_BASE_URL}/Mileage/calculate`, {
                    gigId: row.gigId,
                    origin
                });
                // If the server couldn't reach the route service, the browser asks it
                const miles = res.data.needsRoute
                    ? await routeMiles(res.data.from, res.data.to)
                    : res.data.milesOneWay;
                updateMileageRow(row.gigId, {
                    status: "calculated",
                    miles: String(miles)
                });
            } catch (err) {
                const reason =
                    typeof err.response?.data === "string"
                        ? err.response.data
                        : err.message || "Failed";
                updateMileageRow(row.gigId, { status: "error", error: reason });
            }
        }

        setMileageRunning(false);
        setMileageMsg(
            stopMileage.current
                ? "Stopped."
                : "Done. Check the numbers (you can type over any of them), then click Save."
        );
    };

    const saveMileage = async () => {
        const items = mileageRows
            .filter(
                (r) =>
                    (r.status === "calculated" || r.status === "edited") &&
                    Number(r.miles) > 0
            )
            .map((r) => ({ gigId: r.gigId, milesOneWay: Number(r.miles) }));

        if (items.length === 0) {
            setMileageMsg("Nothing new to save.");
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/Mileage/batch`, {
                originAddress: mileageOrigin.trim(),
                items
            });
            setMileageRows((prev) =>
                prev.map((r) =>
                    (r.status === "calculated" || r.status === "edited") && Number(r.miles) > 0
                        ? { ...r, status: "saved" }
                        : r
                )
            );
            setMileageMsg(`Saved ${items.length} trip${items.length === 1 ? "" : "s"}.`);
            fetchMileage();
        } catch (err) {
            console.error("MILEAGE SAVE ERROR:", err);
            const reason =
                typeof err.response?.data === "string" ? err.response.data : "";
            setMileageMsg(reason ? `Save failed: ${reason}` : "Save failed.");
        }
    };

    const mileageTotals = (() => {
        const counted = mileageRows.filter((r) => Number(r.miles) > 0);
        const oneWay = counted.reduce((sum, r) => sum + Number(r.miles), 0);
        return { trips: counted.length, roundTrip: Math.round(oneWay * 2 * 10) / 10 };
    })();

    const exportMileageCsv = () => {
        const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = [
            [
                "Date",
                "Venue",
                "Venue address",
                "Starting address",
                "One-way miles",
                "Round-trip miles",
                "Purpose"
            ].join(",")
        ];
        mileageRows
            .filter((r) => Number(r.miles) > 0)
            .forEach((r) => {
                const oneWay = Number(r.miles);
                lines.push(
                    [
                        q(new Date(r.date).toLocaleDateString("en-CA")),
                        q(r.venueName),
                        q(r.venueAddress),
                        q(mileageOrigin),
                        oneWay.toFixed(1),
                        (Math.round(oneWay * 2 * 10) / 10).toFixed(1),
                        q("Band performance - travel to gig")
                    ].join(",")
                );
            });
        const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gig-mileage-${mileageYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const mileageYears = Array.from(
        new Set([
            new Date().getFullYear(),
            ...gigs
                .filter((g) => g.startDateTime)
                .map((g) => new Date(g.startDateTime).getFullYear())
        ])
    ).sort((a, b) => b - a);

    const mileageStatusText = (r) => {
        switch (r.status) {
            case "saved": return "Saved";
            case "calculated": return "Calculated (not saved)";
            case "edited": return "Edited (not saved)";
            case "working": return "Calculating…";
            case "noaddr": return "No address: type the miles";
            case "error": return r.error || "Failed";
            default: return "Not calculated";
        }
    };

    // FILTER GIGS
    const now = new Date();

    const upcomingGigs = gigs.filter((gig) => {
        if (!gig.startDateTime) return false;

        return new Date(gig.startDateTime) >= now;
    });

    // DISPLAYED GIGS
    const displayedGigs = showPastGigs
        ? gigs
        : upcomingGigs;

    return (
        <div className="admin-container gigs">
            <AdminMenu setIsAuthenticated={setIsAuthenticated} />

            <div className="container">
                <h2>Upcoming Gigs</h2>

                <button
                    className="add-btn"
                    onClick={openAddModal}
                >
                    + Add Gig
                </button>

                <button
                    className="add-btn"
                    style={{ marginLeft: 8 }}
                    onClick={openMileageModal}
                >
                    Mileage (taxes)
                </button>

                {/* TOGGLE */}
                <div className="gig-toggle">
                    <label>
                        <input type="checkbox" checked={showPastGigs} onChange={(e) =>
                                setShowPastGigs(e.target.checked)
                            }
                        />
                        Show Past Gigs
                    </label>
                </div>

                <div className="gigs-table-container">
                    <table className="gigs-table">
                        <thead>
                            <tr>
                                <th>Event Date</th>
                                <th>Client</th>
                                <th>Client Phone</th>
                                <th>Client Email</th>
                                <th>Venue</th>
                                <th>Address</th>
                                <th>Band Fee</th>
                                <th>Deposit Amount</th>
                                <th>Deposit Paid</th>
                                <th>Deposit Date</th>
                                <th>Booked By:</th>
                                <th>Notes</th>
                                <th>Edit Details</th>
                                <th>Add/View Contract</th>
                            </tr>
                        </thead>

                        <tbody>
                            {displayedGigs.map((gig) => (
                                <tr key={gig.id}>
                                    <td>
                                        {new Date(
                                            gig.startDateTime
                                        ).toLocaleString()}
                                    </td>

                                    <td>{gig.clientName}</td>

                                    <td>{gig.clientPhone}</td>

                                    <td>{gig.clientEmail}</td>

                                    <td>{gig.venueName}</td>

                                    <td>
                                        {gig.venueAddress}
                                        {mileageMap[gig.id] && (
                                            <div style={{ fontSize: "0.8em", opacity: 0.7 }}>
                                                {mileageMap[gig.id].milesRoundTrip} mi round trip
                                            </div>
                                        )}
                                    </td>

                                    <td>{gig.payAmount}</td>

                                    <td>{gig.depositAmount}</td>

                                    <td>
                                        <input type="checkbox" checked={gig.depositPaid} readOnly />
                                    </td>

                                    <td>
                                        {gig.depositPaidDate ? new Date(gig.depositPaidDate).toLocaleDateString() : ""}
                                    </td>

                                    <td>{gig.status}</td>

                                    <td>{gig.notes}</td>

                                    <td>
                                        <button className="edit-btn" onClick={() => openEditModal(gig) }>Edit </button>
                                    </td>

                                    <td>
    {(() => {
        const contractUrl =
            gig.contractUrl || gig.ContractUrl;

        // New uploads are stored as /api/Gigs/contract/<name>.pdf (private folder);
        // older ones are /contracts/<name>.pdf. Both are joined to the site address.
        const fullUrl = contractUrl
            ? `${API_BASE_URL.replace("/api", "")}${contractUrl}`
            : null;

        return (
            <div className="contract-actions">
                {fullUrl ? (
                    <>
                        <button
                            className="view-btn"
                            onClick={() =>
                                window.open(fullUrl, "_blank")
                            }
                        >
                            View PDF
                        </button>

                        <button
                            className="replace-btn"
                            onClick={() =>
                                handleUploadClick(gig.id)
                            }
                        >
                            Replace PDF
                        </button>
                    </>
                ) : (
                    <button
                        className="upload-btn"
                        onClick={() =>
                            handleUploadClick(gig.id)
                        }
                    >
                        Upload PDF
                    </button>
                )}

                <input
                    type="file"
                    accept="application/pdf"
                    style={{ display: "none" }}
                    id={`file-${gig.id}`}
                    onChange={(e) =>
                        handleFileChange(e, gig.id)
                    }
                />
            </div>
        );
    })()}
</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {showModal &&
                    ReactDOM.createPortal(
                        <div className="modal-overlay">
                            <div className="modal">
                                <h3> {isEdit? "Edit Gig": "Add Gig"} </h3>

                                <form
                                    onSubmit={handleSaveGig}
                                    className="modal-form"
                                >
                            <div className="form-group">
                                <label htmlFor="title">Title</label>
                                <input id="title" name="title" value={formGig.title} onChange={handleChange} required />
                            </div>

                            <div className="form-group">
                                <label htmlFor="clientName">Client Name</label>
                                <input id="clientName" name="clientName" value={formGig.clientName} onChange={handleChange} required />
                            </div>

                            <div className="form-group">
                                <label htmlFor="clientPhone">Client Phone</label>
                                <input id="clientPhone" name="clientPhone" value={formGig.clientPhone} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="clientEmail">Client Email</label>
                                <input id="clientEmail" name="clientEmail" type="email" value={formGig.clientEmail} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="venueName">Venue Name</label>
                                <input id="venueName" name="venueName" value={formGig.venueName} onChange={handleChange} required />
                            </div>

                            <div className="form-group">
                                <label htmlFor="venueAddress">Venue Address</label>
                                <input id="venueAddress" name="venueAddress" value={formGig.venueAddress} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="startDateTime">Start Date & Time</label>
                                <input id="startDateTime" type="datetime-local" name="startDateTime" value={formGig.startDateTime} onChange={handleChange} required />
                            </div>

                            <div className="form-group">
                                <label htmlFor="endDateTime">End Date & Time</label>
                                <input id="endDateTime" type="datetime-local" name="endDateTime" value={formGig.endDateTime} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="payAmount">Band Fee</label>
                                <input id="payAmount" type="number" name="payAmount" value={formGig.payAmount || ""} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="depositAmount">Deposit Amount</label>
                                <input id="depositAmount" type="number" name="depositAmount" value={formGig.depositAmount || ""} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="depositPaid">Deposit Paid</label>
                                <input id="depositPaid" type="checkbox" name="depositPaid" checked={formGig.depositPaid || false} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="depositPaidDate">Deposit Date</label>
                                <input id="depositPaidDate" type="date" name="depositPaidDate" value={formGig.depositPaidDate || ""} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="status">Booked By</label>
                                <input id="status" name="status" value={formGig.status} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">Notes</label>
                                <textarea id="notes" name="notes" value={formGig.notes} onChange={handleChange}></textarea>
                            </div>

                                    <div className="modal-buttons">
                                        <button
                                            type="button"
                                            className="cancel-btn"
                                            onClick={closeModal}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            className="save-btn"
                                        >
                                            {isEdit
                                                ? "Save Changes"
                                                : "Add Gig"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>,
                        document.body
                    )}

                {/* MILEAGE MODAL */}
                {showMileage &&
                    ReactDOM.createPortal(
                        <div className="modal-overlay">
                            <div
                                className="modal"
                                style={{ width: "95%", maxWidth: 900, maxHeight: "90vh", overflowY: "auto" }}
                            >
                                <h3>Gig mileage for taxes</h3>

                                <div className="form-group">
                                    <label htmlFor="mileageOrigin">Address you drove from</label>
                                    <input
                                        id="mileageOrigin"
                                        value={mileageOrigin}
                                        onChange={(e) => setMileageOrigin(e.target.value)}
                                        placeholder="Street, city, state ZIP"
                                        disabled={mileageRunning}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="mileageYear">Year</label>
                                    <select
                                        id="mileageYear"
                                        value={mileageYear}
                                        onChange={(e) => setMileageYear(Number(e.target.value))}
                                        disabled={mileageRunning}
                                    >
                                        {mileageYears.map((y) => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={includeUpcoming}
                                            onChange={(e) => setIncludeUpcoming(e.target.checked)}
                                            disabled={mileageRunning}
                                        />{" "}
                                        Include upcoming gigs this year
                                    </label>
                                    <br />
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={recalcAll}
                                            onChange={(e) => setRecalcAll(e.target.checked)}
                                            disabled={mileageRunning}
                                        />{" "}
                                        Recalculate gigs that are already saved
                                    </label>
                                </div>

                                {mileageRows.length === 0 ? (
                                    <p>No gigs found for {mileageYear}.</p>
                                ) : (
                                    <table className="gigs-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Venue</th>
                                                <th>One-way miles</th>
                                                <th>Round trip</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {mileageRows.map((r) => (
                                                <tr key={r.gigId}>
                                                    <td>{new Date(r.date).toLocaleDateString()}</td>
                                                    <td>
                                                        {r.venueName}
                                                        <div style={{ fontSize: "0.8em", opacity: 0.7 }}>
                                                            {r.venueAddress}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            style={{ width: 90 }}
                                                            value={r.miles}
                                                            disabled={mileageRunning}
                                                            onChange={(e) =>
                                                                updateMileageRow(r.gigId, {
                                                                    miles: e.target.value,
                                                                    status: "edited"
                                                                })
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        {Number(r.miles) > 0
                                                            ? (Math.round(Number(r.miles) * 2 * 10) / 10).toFixed(1)
                                                            : ""}
                                                    </td>
                                                    <td>{mileageStatusText(r)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                <p>
                                    <strong>
                                        {mileageTotals.trips} trip{mileageTotals.trips === 1 ? "" : "s"},{" "}
                                        {mileageTotals.roundTrip.toLocaleString()} round-trip miles in {mileageYear}
                                    </strong>
                                </p>

                                {mileageMsg && <p>{mileageMsg}</p>}

                                <div className="modal-buttons">
                                    <button type="button" className="cancel-btn" onClick={closeMileageModal}>
                                        Close
                                    </button>

                                    {mileageRunning ? (
                                        <button
                                            type="button"
                                            className="cancel-btn"
                                            onClick={() => { stopMileage.current = true; }}
                                        >
                                            Stop
                                        </button>
                                    ) : (
                                        <button type="button" className="edit-btn" onClick={runMileage}>
                                            Calculate miles
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="edit-btn"
                                        onClick={exportMileageCsv}
                                        disabled={mileageRunning || mileageTotals.trips === 0}
                                    >
                                        Download CSV
                                    </button>

                                    <button
                                        type="button"
                                        className="save-btn"
                                        onClick={saveMileage}
                                        disabled={mileageRunning}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}
            </div>
        </div>
    );
}